import { createFileRoute, Link } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: "Privacy Notice — JobYoda" },
      { name: "description", content: "How Hypertime LLC (JobYoda) collects, uses, shares, and protects your personal data." },
      { property: "og:title", content: "Privacy Notice — JobYoda" },
      { property: "og:description", content: "How Hypertime LLC (JobYoda) collects, uses, shares, and protects your personal data." },
    ],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function PrivacyPage() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Button component={Link} to="/" size="small" color="inherit" sx={{ mb: 2 }}>
          ← Back to home
        </Button>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Privacy Notice
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </Typography>
        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" color="text.secondary">
          This Privacy Notice explains how Hypertime LLC ("we", "us"), operating the
          JobYoda service ("JobYoda", the "service"), collects, uses, shares, and
          protects your personal data. Hypertime LLC is the data controller for personal
          data processed through the service.
        </Typography>

        <Section title="1. Personal data we collect">
          We collect the following categories of personal data:
          <ul>
            <li><strong>Account data</strong> — name, email address, login credentials, authentication identifiers.</li>
            <li><strong>Profile data</strong> — resume content, LinkedIn data, GitHub username and metrics, portfolio links, target role, location, work authorization, headline, and any other information you upload or enter.</li>
            <li><strong>Application data</strong> — job descriptions you save, generated materials (resumes, cover letters, Q&A), application tracking entries, notes, and interview session recordings/transcripts.</li>
            <li><strong>Communications</strong> — emails you forward to your JobYoda inbox, support messages.</li>
            <li><strong>Usage and telemetry</strong> — pages visited, features used, error logs, device identifiers, IP address, browser/user-agent.</li>
            <li><strong>Optional demographic data</strong> — only if you choose to provide it for application autofill (e.g. pronouns, veteran status, disability status).</li>
          </ul>
        </Section>

        <Section title="2. Purposes and legal basis">
          We process personal data for the following purposes:
          <ul>
            <li><strong>Providing the service</strong> (contract performance) — account creation, profile ingestion, job matching, generating tailored materials, tracking applications, and learning loops.</li>
            <li><strong>Security and fraud prevention</strong> (legitimate interests) — protecting accounts, detecting abuse, rate-limiting.</li>
            <li><strong>Service improvement</strong> (legitimate interests) — analyzing aggregate usage to improve features and AI quality.</li>
            <li><strong>Customer support</strong> (contract performance / legitimate interests) — responding to your requests.</li>
            <li><strong>Legal compliance</strong> (legal obligation) — tax records, responding to lawful requests.</li>
            <li><strong>Marketing</strong> (consent) — only where you have opted in.</li>
          </ul>
        </Section>

        <Section title="3. Data sharing">
          We share personal data with the following categories of recipients:
          <ul>
            <li><strong>Service providers / subprocessors</strong> — cloud hosting and database (Lovable Cloud / Supabase), AI model providers (used through the Lovable AI Gateway) for generating materials and analyses, and email/transcription providers strictly to deliver the service.</li>
            <li><strong>Payment processing</strong> — <a href="https://stripe.com" target="_blank" rel="noreferrer" style={{ color: "inherit", fontWeight: 600 }}>Stripe</a> processes all card payments on our behalf. We share only the information Stripe needs to bill you (email, billing address, plan). Stripe handles card details directly — we never receive or store full card numbers. See Stripe's privacy policy at <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" style={{ color: "inherit", fontWeight: 600 }}>stripe.com/privacy</a>.</li>
            <li><strong>Professional advisers</strong> — legal, accounting, and compliance advisers under confidentiality.</li>
            <li><strong>Authorities</strong> — where required by law or to protect rights, property, or safety.</li>
          </ul>
          We do not sell your personal data.
        </Section>

        <Section title="4. International transfers">
          Personal data may be processed in countries outside your own, including the
          United States. Where required, we rely on appropriate safeguards such as the
          European Commission's Standard Contractual Clauses or equivalent mechanisms
          for transfers from the UK/EEA.
        </Section>

        <Section title="5. Retention">
          We keep personal data only as long as needed for the purposes above. Account
          and profile data are kept while your account is active and deleted (or
          anonymised) on account deletion. Billing records are retained as required by
          tax and accounting law (typically 6–10 years). Backups are purged on a rolling
          basis.
        </Section>

        <Section title="6. Your rights">
          Depending on your jurisdiction, you may have the right to access, correct,
          delete, restrict, port, or object to processing of your personal data, and to
          withdraw consent at any time. You can exercise these rights from your account
          settings or by contacting us via the <Link to="/support" style={{ color: "inherit", fontWeight: 600 }}>support page</Link>.
          UK/EEA users have the right to complain to their local supervisory authority.
          We aim to respond within one month.
        </Section>

        <Section title="7. Security">
          We implement appropriate technical and organisational measures to protect
          personal data, including encryption in transit (TLS), encryption at rest,
          access controls, row-level security on the database, and least-privilege
          service credentials. No system is perfectly secure — please use a strong,
          unique password and keep your credentials confidential.
        </Section>

        <Section title="8. Cookies">
          We use strictly necessary cookies for authentication and session management.
          We may use limited first-party analytics cookies to understand aggregate
          usage. We do not use third-party advertising cookies. You can manage cookies
          through your browser settings.
        </Section>

        <Section title="9. Children">
          The service is not directed to children under 16 and we do not knowingly
          collect personal data from them.
        </Section>

        <Section title="10. Changes">
          We may update this Privacy Notice. Material changes will be communicated by
          email or in-app notice. The "Last updated" date above reflects the latest
          revision.
        </Section>

        <Section title="11. Contact">
          Hypertime LLC — questions about this notice or your data? Reach us via our{" "}
          <Link to="/support" style={{ color: "inherit", fontWeight: 600 }}>support page</Link>.
        </Section>

        <Divider sx={{ my: 5 }} />
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={Link} to="/terms" variant="outlined">Terms</Button>
          <Button component={Link} to="/refunds" variant="outlined">Refund Policy</Button>
          <Button component={Link} to="/support" variant="outlined">Support</Button>
          <Button component={Link} to="/signup" variant="contained">Get started</Button>
        </Stack>
      </Container>
    </Box>
  );
}
